import * as Yup from "yup";
import CircularProgress from "@material-ui/core/CircularProgress";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import DateFnsAdapter from "@material-ui/pickers/adapter/date-fns";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import HelpIcon from "@material-ui/icons/Help";
import MuiButton from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import enLocale from "date-fns/locale/en-US";
import React, { FC, useEffect, useState } from "react";
import { Field, Form, Formik, FormikHelpers } from "formik";
import { InputAttributes, NumberFormatBaseProps, NumericFormat } from "react-number-format";
import { DatePicker as MUIDatePicker, DatePickerProps, LocalizationProvider } from "@material-ui/pickers";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";

import BaseInput from "../../../../../components/elements/BaseInput";
import CheckBox from "../../../../../components/elements/CheckBox";
import FormFieldError from "../../../../../components/elements/FormFieldError";
import Select from "../../../../../components/elements/Select";
import api from "../../../../../api";
import { BrandgenCampaignPool, CampaignMediaTypeEnum } from "../../../../../types/campaignTypes";
import { MAX_CAMPAIGN_NAME_LENGTH } from "../../../../../constants/campaignValidation";
import {
  calculateEstimatedBudget,
  endDateCheckErrorsOnChange,
  maxBidCheckErrorsOnChange,
  startDateCheckErrorsOnChange,
} from "../../../../../utils/yupValidationUtils";
import { mediaTypeOptions } from "../../../../../views/CampaignCreationBasicSlides/AdGroupSelectionSlide/components/MediaTypeSelector/MediaTypeSelector";
import { parseCampaignDate, toCampaignDateISO } from "../../../../../utils/campaignDateUtils";

const tooltips = {
  showInHubspot:
    "Checking this option will allow a HubSpot user to add and remove a Company to the Campaign. This will be available to All HubSpot users within your account who have permissions to access the Company Record",
  mediaType: (
    <>
      <Typography color="inherit">Please Note</Typography>
      <br />
      Currently, you can only choose display campaigns (image/gif) <em>or</em> video campaigns.
      <br />
      <br />
      <b>If you select video,</b> you will only be able to upload video creatives later in the campaign creation
      process.
    </>
  ),
  maxCompanies:
    "The maximum number of target companies that can be added to this campaign. If left empty or at 0, no limit will be imposed.",
  estimatedBudget: "The Estimated Budget is the Daily Budget multiplied by the number of days the campaign is running.",
};

const outlinedFieldStyles = ({ theme }: { theme: Theme }) => ({
  "& .MuiOutlinedInput-root": {
    background: theme.palette.brand.white,
    borderRadius: 8,
    "& fieldset": {
      borderColor: theme.palette.brand.gray200,
    },
    "&:hover fieldset": {
      borderColor: theme.palette.brand.gray300,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.brand.blueAccent,
    },
  },
  "& .MuiInputBase-root": {
    fontSize: 13,
  },
  "& .MuiInputLabel-outlined": {
    fontSize: 13,
  },
  "& label.Mui-focused": {
    color: theme.palette.brand.blueAccent,
  },
});

const ModalHeader = styled("div")(({ theme }: { theme: Theme }) => ({
  padding: "20px 24px",
  borderBottom: `1px solid ${theme.palette.brand.gray100}`,
  background: theme.palette.brand.white,
}));

const ModalTitle = styled(Typography)(({ theme }: { theme: Theme }) => ({
  fontSize: 16,
  fontWeight: 600,
  color: theme.palette.brand.gray900,
}));

const StyledDialogContent = styled(DialogContent)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.gray50,
  padding: "24px !important",
  paddingBottom: "0 !important",
  width: 600,
  maxWidth: "100%",
}));

const TwoColumnInputs = styled("div")({
  display: "flex",
  gap: 16,
  alignItems: "flex-start",
});

const InputField = styled("div")({
  width: "100%",
  marginBottom: 8,
  height: 74,
  "& > div": {
    marginBottom: 1,
  },
});

const InputFieldWide = styled(InputField)({
  width: 350,
});

const TextAreaField = styled("div")({
  width: "100%",
  marginBottom: 16,
  height: 155,
  "& > div": {
    marginBottom: 1,
  },
});

const CheckboxInput = styled("div")({
  width: "100%",
  paddingLeft: 10,
  "& > div": {
    marginBottom: 1,
  },
});

const InputWithTooltip = styled("div")({
  width: "100%",
  display: "flex",
  alignItems: "center",
  "&:nth-child(2)": {
    marginLeft: 20,
  },
});

const EstimatedBudgetWithTooltip = styled("div")({
  width: "100%",
  display: "flex",
  alignItems: "center",
  marginRight: 20,
});

const ActiveCheckbox = styled("div")({
  marginTop: 7,
});

const ErrorSlot = styled("div")({
  minHeight: 30,
});

const DeactivationErrorText = styled("p")(({ theme }: { theme: Theme }) => ({
  color: theme.palette.brand.errorRed,
  margin: "0 0 5px 0",
  fontSize: 13,
}));

const StyledHelpIcon = styled(HelpIcon)(({ theme }: { theme: Theme }) => ({
  fill: theme.palette.brand.gray500,
  marginTop: -18,
  marginLeft: 8,
}));

const HelpIconAligned = styled(HelpIcon)(({ theme }: { theme: Theme }) => ({
  fill: theme.palette.brand.gray500,
  marginTop: -45,
  marginLeft: 8,
}));

const StyledDialogActions = styled(DialogActions)(({ theme }: { theme: Theme }) => ({
  padding: "16px 24px",
  borderTop: `1px solid ${theme.palette.brand.gray100}`,
  background: theme.palette.brand.white,
}));

const CancelButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  color: theme.palette.brand.gray700,
  textTransform: "none",
  fontWeight: 600,
  fontSize: 13,
  borderColor: theme.palette.brand.gray300,
  borderRadius: 8,
  padding: "8px 14px",
  minWidth: 85,
  height: 40,
  "&:hover": {
    background: theme.palette.brand.gray50,
    borderColor: theme.palette.brand.gray300,
  },
}));

const SubmitButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.coral,
  color: theme.palette.brand.white,
  textTransform: "none",
  fontWeight: 600,
  fontSize: 13,
  borderRadius: 8,
  padding: "8px 16px",
  minWidth: 85,
  height: 40,
  boxShadow: "none",
  "&:hover": {
    background: theme.palette.brand.coralHover,
    boxShadow: "none",
    transform: "translateY(-1px)",
  },
  "&.Mui-disabled": {
    background: theme.palette.brand.gray200,
    color: theme.palette.brand.gray500,
  },
}));

const StyledBaseInput = styled(BaseInput)(({ theme }: { theme: Theme }) => outlinedFieldStyles({ theme }));

const StyledSelect = styled(Select)(({ theme }: { theme: Theme }) => ({
  width: "100%",
  ...outlinedFieldStyles({ theme }),
  "& .MuiInputBase-root": {
    height: 48,
  },
}));

interface NumberFormatInputProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

const StyledCurrencyFormatInput = React.forwardRef<NumberFormatBaseProps<InputAttributes>, NumberFormatInputProps>(
  function StyledCurrencyFormatCustom(props, ref) {
    const { onChange, ...rest } = props;

    return (
      <NumericFormat
        {...rest}
        customInput={StyledBaseInput}
        getInputRef={ref}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: values.value,
            },
          });
        }}
        thousandSeparator
        valueIsNumericString
        prefix="$"
      />
    );
  },
);

const EditCampaignDatePicker: FC<DatePickerProps> = ({ value, onChange, InputProps, label, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleClickAway = () => {
    if (isOpen) {
      setIsOpen(false);
    }
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div>
        <LocalizationProvider dateAdapter={DateFnsAdapter} locale={enLocale}>
          <MUIDatePicker
            open={isOpen}
            onClose={() => setIsOpen(false)}
            onOpen={() => setIsOpen(true)}
            InputProps={InputProps}
            label={label}
            value={value}
            onChange={onChange}
            renderInput={(inputProps) => <StyledBaseInput variant="outlined" {...inputProps} />}
            disabled={disabled}
          />
        </LocalizationProvider>
      </div>
    </ClickAwayListener>
  );
};

type EditCampaignDetailsModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  campaignToEdit: BrandgenCampaignPool;
  fetchAndSetCampaings: () => void;
};

type FormInitialValues = {
  name: string;
  description: string;
  mediaType: "video" | "display";
  dailyBudget: number | string;
  estimatedBudget: number | string;
  maxBid: number | string;
  startDate: string | Date;
  endDate: string | Date;
  showInHubspot: boolean;
  maxCompanies: number | string;
  isActive: boolean;
};

const EditCampaignDetailsModalRedesign: FC<EditCampaignDetailsModalProps> = ({
  isOpen,
  handleClose,
  campaignToEdit,
  fetchAndSetCampaings,
}) => {
  const formInitialValues: FormInitialValues = {
    name: campaignToEdit.name,
    description: campaignToEdit.description ?? "",
    mediaType: campaignToEdit.isVideoCampaign ? CampaignMediaTypeEnum.VIDEO : CampaignMediaTypeEnum.DISPLAY,
    dailyBudget: campaignToEdit.daily_budget,
    estimatedBudget: campaignToEdit.campaign_budget,
    maxBid: campaignToEdit.maxBid === null ? 0 : campaignToEdit.maxBid,
    startDate: toCampaignDateISO(campaignToEdit.start_date),
    endDate: toCampaignDateISO(campaignToEdit.end_date),
    showInHubspot: campaignToEdit.showInHubspot,
    maxCompanies: campaignToEdit.maxCompanies,
    isActive: campaignToEdit.active,
  };

  const validationSchema = Yup.object().shape(
    {
      name: Yup.string()
        .required("Required field.")
        .test(
          "pixel-length-check",
          (value) =>
            `Campaign name is too long. Maximum length is ${MAX_CAMPAIGN_NAME_LENGTH} characters (current: ${
              value?.value?.length || 0
            }). `,
          (value) => {
            if (!value) return true;
            return `${value} Pixel`.length <= 100;
          },
        ),
      mediaType: Yup.string().required("Required field."),
      dailyBudget: Yup.number().positive("Number must be positive").required("Required field."),
      maxBid: Yup.number()
        .positive("Number must be positive!")
        .max(100, "Max Bid can not exceed $100")
        .when("estimatedBudget", (estimatedBudget: number, schema: any) => {
          return schema.max(estimatedBudget, "Max Bid can not be greater than the Estimated Budget");
        })
        .required("Required field."),
      startDate: Yup.date().when("startDate", (val: any, schema: any) => {
        const startDateTime = new Date(val).getTime();
        const todayDateTime = new Date().getTime();
        const isStartDateInFuture = startDateTime > todayDateTime;

        if (isStartDateInFuture) {
          return schema.min(
            new Date(new Date().setDate(new Date().getDate() - 1)),
            "Please choose a future or current date!",
          );
        }
        return schema;
      }),
      endDate: Yup.date().when("startDate", (startDate: any, schema: any) => {
        if (startDate) {
          const dayAfter = new Date(startDate.getTime() + 86400000);
          return (
            schema.min(dayAfter, "The end date must be after the start date") &&
            schema.min(
              new Date(new Date().setDate(new Date().getDate() - 1)),
              "Please choose a future or current date!",
            )
          );
        }

        return schema;
      }),
    },
    [["startDate", "startDate"]],
  );

  const startDateTime = new Date(formInitialValues.startDate).getTime();
  const todayDateTime = new Date().getTime();
  const isStartDateInFuture = startDateTime > todayDateTime;

  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  const handleSubmit = async (values: FormInitialValues, actions: FormikHelpers<FormInitialValues>) => {
    try {
      setIsLoading(true);
      const {
        name,
        description,
        mediaType,
        dailyBudget,
        estimatedBudget,
        maxBid,
        startDate,
        endDate,
        showInHubspot,
        maxCompanies,
        isActive,
      } = values;
      await api.updateCampaign(campaignToEdit.brandgenCampaignPoolId, {
        name,
        description,
        daily_budget: +dailyBudget,
        campaign_budget: +estimatedBudget,
        maxBid: +maxBid,
        start_date: toCampaignDateISO(startDate),
        end_date: toCampaignDateISO(endDate),
        active: isActive,
        maxCompanies: +maxCompanies,
        showInHubspot,
        isVideoCampaign: mediaType === "video",
      });
      setIsLoading(false);
      handleClose();
      fetchAndSetCampaings();
    } catch (error) {
      const err = error as any;
      setIsLoading(false);
      actions.resetForm();
      setErrorMessage(`There was an error: ${err.message}`);
    }
  };

  useEffect(() => {
    const today = new Date();
    const targetDate = new Date(campaignToEdit.end_date);

    today.setHours(0, 0, 0, 0);
    targetDate.setHours(0, 0, 0, 0);

    const diffTime = today.getTime() - targetDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays >= 175) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  }, [campaignToEdit.end_date]);

  return (
    <Dialog
      open={isOpen}
      onClose={isLoading ? undefined : handleClose}
      aria-labelledby="edit-campaign-details-modal-title"
      maxWidth={false}
      disableEnforceFocus
    >
      <Formik
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnBlur={false}
      >
        {({ setFieldValue, values, setFieldError }) => (
          <>
            <ModalHeader>
              <ModalTitle id="edit-campaign-details-modal-title" variant="h2">
                Edit Campaign Details
              </ModalTitle>
            </ModalHeader>
            <Form>
              <StyledDialogContent>
                <CheckboxInput>
                  <Field
                    as={FormControlLabel}
                    name="showInHubspot"
                    type="checkbox"
                    control={<CheckBox checked={values.showInHubspot} label="Sales Enabled Campaign" />}
                    disabled={isDisabled}
                  />

                  <Tooltip title={tooltips.showInHubspot}>
                    <StyledHelpIcon />
                  </Tooltip>
                </CheckboxInput>

                <InputField>
                  <Field
                    as={StyledBaseInput}
                    value={values.name}
                    error={!!errorMessage}
                    name="name"
                    variant="outlined"
                    label="Name *"
                    disabled={isDisabled}
                  />

                  <ErrorSlot>
                    <FormFieldError name="name" />
                  </ErrorSlot>
                </InputField>

                <TextAreaField>
                  <Field
                    as={StyledBaseInput}
                    value={values.description}
                    name="description"
                    variant="outlined"
                    label="Description"
                    rows={4}
                    multiline
                    disabled={isDisabled}
                  />

                  <ErrorSlot>
                    <FormFieldError name="description" />
                  </ErrorSlot>
                </TextAreaField>

                <TwoColumnInputs>
                  <InputWithTooltip>
                    <InputField>
                      <Field
                        as={StyledSelect}
                        value={values.mediaType}
                        name="mediaType"
                        label="Campaign Media Type"
                        options={mediaTypeOptions}
                        disabled
                      />
                    </InputField>

                    <Tooltip title={tooltips.mediaType}>
                      <HelpIconAligned />
                    </Tooltip>
                  </InputWithTooltip>

                  {values.showInHubspot ? (
                    <InputWithTooltip>
                      <InputField>
                        <Field
                          as={StyledBaseInput}
                          value={values.maxCompanies}
                          name="maxCompanies"
                          variant="outlined"
                          label="Max Companies"
                          type="number"
                          InputProps={{
                            inputProps: {
                              min: 0,
                            },
                          }}
                          disabled={isDisabled}
                        />

                        <ErrorSlot>
                          <FormFieldError name="maxCompanies" />
                        </ErrorSlot>
                      </InputField>
                      <Tooltip title={tooltips.maxCompanies}>
                        <HelpIconAligned />
                      </Tooltip>
                    </InputWithTooltip>
                  ) : null}
                </TwoColumnInputs>

                <TwoColumnInputs>
                  <InputField>
                    <Field
                      as={EditCampaignDatePicker}
                      value={parseCampaignDate(values.startDate)}
                      variant="outlined"
                      label="Campaign Start Date *"
                      InputProps={{
                        name: "startDate",
                      }}
                      onChange={(date: Date) => {
                        setFieldValue("startDate", toCampaignDateISO(date), false);
                        const budget = calculateEstimatedBudget(date, values.endDate as Date, +values.dailyBudget);
                        setFieldValue("estimatedBudget", budget, false);
                        startDateCheckErrorsOnChange(date, setFieldError);
                        endDateCheckErrorsOnChange(date, values.endDate as Date, setFieldError);
                        maxBidCheckErrorsOnChange(values.maxBid as number, budget, setFieldError);
                      }}
                      disabled={!isStartDateInFuture || isDisabled}
                    />
                    <ErrorSlot>
                      <FormFieldError name="startDate" />
                    </ErrorSlot>
                  </InputField>

                  <InputField>
                    <Field
                      as={EditCampaignDatePicker}
                      value={parseCampaignDate(values.endDate)}
                      variant="outlined"
                      label="Campaign End Date *"
                      InputProps={{
                        name: "endDate",
                      }}
                      onChange={(date: Date) => {
                        setFieldValue("endDate", toCampaignDateISO(date), false);
                        const budget = calculateEstimatedBudget(values.startDate as Date, date, +values.dailyBudget);
                        setFieldValue("estimatedBudget", budget, false);
                        endDateCheckErrorsOnChange(values.startDate as Date, date, setFieldError);
                        maxBidCheckErrorsOnChange(values.maxBid as number, budget, setFieldError);
                      }}
                      disabled={isDisabled}
                    />
                    <ErrorSlot>
                      <FormFieldError name="endDate" />
                    </ErrorSlot>
                  </InputField>
                </TwoColumnInputs>

                <TwoColumnInputs>
                  <InputField>
                    <Field
                      as={StyledCurrencyFormatInput}
                      value={values.dailyBudget}
                      name="dailyBudget"
                      variant="outlined"
                      label="Daily Budget $ *"
                      InputLabelProps={{ shrink: !!values.dailyBudget.toString() }}
                      onChange={({ target }: { target: any }) => {
                        const budget = calculateEstimatedBudget(
                          values.startDate as Date,
                          values.endDate as Date,
                          +target.value,
                        );
                        setFieldValue("estimatedBudget", budget, false);
                        setFieldValue("dailyBudget", target.value, false);
                        maxBidCheckErrorsOnChange(values.maxBid as number, budget, setFieldError);
                        if ((target.value as number) > 0 && target.value) {
                          setFieldError("dailyBudget", undefined);
                        }
                      }}
                      disabled={isDisabled}
                    />

                    <ErrorSlot>
                      <FormFieldError name="dailyBudget" />
                    </ErrorSlot>
                  </InputField>

                  <EstimatedBudgetWithTooltip>
                    <InputField>
                      <Field
                        as={StyledCurrencyFormatInput}
                        value={values.estimatedBudget}
                        name="estimatedBudget"
                        variant="outlined"
                        label="Estimated Budget $ *"
                        disabled
                        InputLabelProps={{ shrink: !!values.estimatedBudget.toString() }}
                      />
                    </InputField>

                    <Tooltip title={tooltips.estimatedBudget}>
                      <HelpIconAligned />
                    </Tooltip>
                  </EstimatedBudgetWithTooltip>

                  <InputFieldWide>
                    <Field
                      as={StyledCurrencyFormatInput}
                      value={values.maxBid}
                      name="maxBid"
                      variant="outlined"
                      label="Max Bid $ *"
                      InputLabelProps={{ shrink: !!values.maxBid.toString() }}
                      onChange={({ target }: any) => {
                        setFieldValue("maxBid", target.value, false);
                        maxBidCheckErrorsOnChange(
                          target.value as number,
                          values.estimatedBudget as number,
                          setFieldError,
                        );
                      }}
                      disabled={isDisabled}
                    />

                    <ErrorSlot>
                      <FormFieldError name="maxBid" />
                    </ErrorSlot>
                  </InputFieldWide>
                </TwoColumnInputs>

                <ActiveCheckbox>
                  <CheckboxInput>
                    <Field
                      as={FormControlLabel}
                      name="isActive"
                      type="checkbox"
                      control={<CheckBox checked={values.isActive} label="Active" disabled={isDisabled} />}
                    />
                  </CheckboxInput>
                </ActiveCheckbox>

                {isDisabled && (
                  <DeactivationErrorText>
                    This campaign was deactivated over 175 days ago and can no longer be reactivated. Please create a
                    new campaign instead.
                  </DeactivationErrorText>
                )}
              </StyledDialogContent>
              <StyledDialogActions>
                <CancelButton variant="outlined" onClick={handleClose} disabled={isLoading}>
                  Cancel
                </CancelButton>
                <SubmitButton type="submit" variant="contained" disabled={isDisabled || isLoading}>
                  {isLoading ? <CircularProgress size={20} style={{ color: "inherit" }} /> : "Submit"}
                </SubmitButton>
              </StyledDialogActions>
            </Form>
          </>
        )}
      </Formik>
    </Dialog>
  );
};

export default EditCampaignDetailsModalRedesign;
